import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { FiSearch, FiChevronRight, FiMessageCircle, FiMail, FiLoader, FiAlertCircle, FiCalendar } from 'react-icons/fi'
import { RootState, AppDispatch } from '@/store/store'
import {
    fetchPublishedPosts,
    searchBlogs,
    fetchByCategory,
    fetchCategories,
    clearSearch
} from '@/store/slices/blogSlice'
import { useGlobalChat } from '@/contexts/ChatContext'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { getCategoryName, formatBlogDate } from '@/utils/blogHelpers'
import { IBlogPost } from '@/types/blog'

const HelpCenterPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()
    const { openChat } = useGlobalChat()
    const [searchQuery, setSearchQuery] = useState('')
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery)
    const [selectedCategory, setSelectedCategory] = useState('all')

    const {
        posts,
        isLoading,
        error,
        pagination,
        categories,
        isLoadingCategories
    } = useSelector((state: RootState) => state.blog)

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery)
        }, 500)
        return () => clearTimeout(timer)
    }, [searchQuery])

    // Fetch categories and initial posts on mount
    useEffect(() => {
        dispatch(fetchCategories())
        dispatch(fetchPublishedPosts({ page: 1, limit: 10 }))
    }, [dispatch])

    // Handle search and category changes
    useEffect(() => {
        if (debouncedSearchQuery.trim()) {
            dispatch(searchBlogs({
                query: debouncedSearchQuery,
                filters: { page: 1, limit: 10, category: selectedCategory !== 'all' ? selectedCategory : undefined }
            }))
        } else if (selectedCategory !== 'all') {
            dispatch(fetchByCategory({
                category: selectedCategory,
                filters: { page: 1, limit: 10 }
            }))
        } else {
            dispatch(fetchPublishedPosts({ page: 1, limit: 10 }))
        }
    }, [debouncedSearchQuery, selectedCategory, dispatch])

    const handleCategoryChange = (categoryId: string) => {
        setSelectedCategory(categoryId)
        setSearchQuery('')
        dispatch(clearSearch())
    }

    const handleReadMore = (post: IBlogPost) => {
        navigate(`/blog/${post.slug}`)
    }

    const handleStartChat = () => {
        openChat()
    }

    const handleSendEmail = () => {
        window.location.href = 'mailto:devsidego@gmail.com?subject=Help%20Request&body=Please%20describe%20your%20question%20or%20issue...'
    }

    // Build categories array from fetched data
    const displayCategories = [
        { id: 'all', label: 'All Articles', count: pagination?.totalCount ?? 0 }
    ]

    if (Array.isArray(categories) && categories.length > 0) {
        categories.forEach(cat => {
            const catName = typeof cat === 'object' ? getCategoryName(cat) : cat
            const catId = typeof cat === 'object' ? cat._id : cat
            displayCategories.push({
                id: catId,
                label: catName.charAt(0).toUpperCase() + catName.slice(1),
                count: posts.filter(p => getCategoryName(p.category) === catName).length
            })
        })
    }

    // Get featured articles by filtering posts with featured: true
    const popularArticles = posts.filter(post => post.featured).slice(0, 3)

    const contactOptions = [
        {
            icon: <FiMessageCircle className="w-6 h-6" />,
            title: 'Live Chat',
            description: 'Get instant help from our support team',
            availability: 'Available 24/7',
            action: 'Start Chat',
            onClick: handleStartChat
        },
        {
            icon: <FiMail className="w-6 h-6" />,
            title: 'Email Support',
            description: 'Send us a detailed message',
            availability: 'Response within 24 hours',
            action: 'Send Email',
            onClick: handleSendEmail
        }
    ]

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            {/* Hero Section */}
            <section className="bg-gray-100 dark:bg-gray-800 py-20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h1 className="heading-hero mb-6 text-gray-900 dark:text-white">How can we help you?</h1>
                    <p className="text-body-lg mb-8 text-gray-700 dark:text-gray-300">
                        Find answers to common questions, learn how to use SideGO effectively,
                        and get the support you need to succeed.
                    </p>

                    {/* Search Bar */}
                    <div className="max-w-xl mx-auto">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Search for help articles..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full rounded-lg border-0 py-2.5 pl-12 pr-3 text-lg text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 dark:bg-gray-800 dark:text-gray-100 dark:ring-gray-600 dark:placeholder:text-gray-500 dark:focus:ring-primary-500"
                            />
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                {isLoading ? (
                                    <FiLoader className="h-6 w-6 text-gray-400 dark:text-gray-500 animate-spin" />
                                ) : (
                                    <FiSearch className="h-6 w-6 text-gray-400 dark:text-gray-500" />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Categories Section */}
            <section className="py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="heading-section mb-4 text-gray-900 dark:text-white">Browse by Category</h2>
                        <p className="text-body-lg text-gray-700 dark:text-gray-300">
                            Select a category to find specific help articles
                        </p>
                    </div>

                    {/* Category Tabs */}
                    <div className="flex flex-wrap justify-center gap-2 mb-12 pb-8 border-b border-gray-200 dark:border-gray-700">
                        {isLoadingCategories ? (
                            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                                <FiLoader className="w-4 h-4 animate-spin" />
                                <span className="text-sm">Loading categories...</span>
                            </div>
                        ) : (
                            displayCategories.map((category) => (
                                <button
                                    key={category.id}
                                    onClick={() => handleCategoryChange(category.id)}
                                    className={`px-4 py-2 rounded-full text-ui transition-colors duration-200 ${selectedCategory === category.id
                                        ? 'bg-primary-600 dark:bg-primary-500 text-white font-medium'
                                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                                        }`}
                                >
                                    {category.label} ({category.count})
                                </button>
                            ))
                        )}
                    </div>
                </div>
            </section>

            {/* Popular Articles Section */}
            {popularArticles.length > 0 && selectedCategory === 'all' && !debouncedSearchQuery && (
                <section className="bg-gray-100 dark:bg-gray-800 py-16">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-12">
                            <h2 className="heading-section mb-4 text-gray-900 dark:text-white">Popular Articles</h2>
                            <p className="text-body-lg text-gray-700 dark:text-gray-300">
                                Most viewed and helpful articles
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {popularArticles.map((article) => (
                                <div key={article._id} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md hover:border-primary-300 dark:hover:border-primary-500 transition-all duration-200 cursor-pointer" onClick={() => handleReadMore(article)}>
                                    <div className="flex items-center justify-between gap-2 mb-3">
                                        <div className="flex items-center gap-2">
                                            {article.featured && (
                                                <span className="inline-block bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap">
                                                    ⭐ Featured
                                                </span>
                                            )}
                                        </div>
                                        <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">{article.readingTime || 5} min read</span>
                                    </div>
                                    <h3 className="heading-card mb-2 text-gray-900 dark:text-white line-clamp-2">{article.title}</h3>
                                    <p className="text-body mb-4 text-gray-600 dark:text-gray-400 line-clamp-2">{article.excerpt}</p>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                                            <FiCalendar className="w-3 h-3 mr-1" />
                                            {formatBlogDate(article.publishedAt)}
                                        </div>
                                        <button className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-ui font-medium flex items-center gap-1">
                                            Read <FiChevronRight className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* All Articles Section */}
            <section className="py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Error State */}
                    {error && !isLoading && (
                        <div className="mb-8 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 flex items-start gap-4">
                            <FiAlertCircle className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                            <div>
                                <h3 className="font-semibold text-red-800 dark:text-red-300 mb-1">Error Loading Articles</h3>
                                <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
                            </div>
                        </div>
                    )}

                    {/* Loading State */}
                    {isLoading ? (
                        <div className="flex justify-center items-center py-16">
                            <LoadingSpinner />
                        </div>
                    ) : posts.length === 0 ? (
                        <div className="text-center py-12">
                            <p className="text-gray-600 dark:text-gray-400 text-lg">
                                {debouncedSearchQuery ? 'No articles found matching your search.' : 'No articles available at this time.'}
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {posts.map((article) => (
                                <div key={article._id} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md hover:border-primary-300 dark:hover:border-primary-500 transition-all duration-200 cursor-pointer" onClick={() => handleReadMore(article)}>
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1">
                                            <div className="flex items-start mb-2 gap-3">
                                                <div className="flex-1">
                                                    <h3 className="heading-card text-gray-900 dark:text-white line-clamp-2">{article.title}</h3>
                                                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                                                        <span className="inline-block bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs px-2 py-1 rounded-full capitalize">
                                                            {getCategoryName(article.category)}
                                                        </span>
                                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                                            {article.readingTime || 5} min read
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <p className="text-body mb-3 text-gray-600 dark:text-gray-400 line-clamp-2">{article.excerpt}</p>
                                            <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                                                <FiCalendar className="w-3 h-3 mr-1" />
                                                {formatBlogDate(article.publishedAt)}
                                            </div>
                                        </div>
                                        <button aria-label={`Read article ${article.title}`} className="ml-4 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors flex-shrink-0">
                                            <FiChevronRight className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Contact Support Section */}
            <section className="bg-gray-100 dark:bg-gray-800 py-16">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="heading-section mb-4 text-gray-900 dark:text-white">Still Need Help?</h2>
                        <p className="text-body-lg text-gray-700 dark:text-gray-300">
                            Can't find what you're looking for? Our support team is here to help.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {contactOptions.map((option, index) => (
                            <div key={index} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center hover:shadow-md transition-shadow duration-200">
                                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full mb-6">
                                    {option.icon}
                                </div>
                                <h3 className="heading-card mb-3 text-gray-900 dark:text-white">{option.title}</h3>
                                <p className="text-body mb-2 text-gray-600 dark:text-gray-400">{option.description}</p>
                                <p className="text-ui-sm text-gray-500 dark:text-gray-500 mb-6">
                                    {option.availability}
                                </p>
                                <button
                                    onClick={option.onClick}
                                    className="btn-primary w-full"
                                >
                                    {option.action}
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    )
}

export default HelpCenterPage